import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {navigator} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import store from '../store';
import {setSection, setHighlightFeatures, setMedia, setSettingsActive, toggleSettingsActive} from '../slices/statusSlice';
import {getProductData} from '../util/getProductData';
import {isSatisfied, getValueOfFeature} from '../util/values';
import logo from '../../icons/selux-logo';
import close from '../../icons/close';
import overview from '../../icons/overview';
import gear from '../../icons/gear';


import {selectUnsatisfied, selectFeatureModelCompletionPercentage, selectLightProfilePercentage, selectLightProfileIsSatisfied} from '../selectors';
import {routeUrl} from '../util/routeUrl';

@customElement('sections-head')
export class SectionsHead extends connect(store)(navigator(LitElement)) {

	static styles = [
		css`
			:host {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: space-between;
				margin: 0 var(--small-padding);
				padding: var(--padding) 0 var(--large-padding) ;
				gap: 10px
			}

			@media (min-width: 768px) {
				:host {
					margin: 0 var(--large-padding);
				}
			}

			.toolbar {
				display: flex;
				flex-direction: row;
				gap: 8px;
				margin-left: auto;
			}

			.back {
				height: 23px;
				display: block;
				font-family: var(--font-family-regular);
				font-size: var(--font-size-widget);
				color: var(--color-white);
				text-transform: uppercase;
				letter-spacing: 1px;
				text-decoration: none;
				padding: 0;
			}

			.back svg {
				height: 100%;
				fill: currentColor;
			}

			.settings {
				display: flex;
				position: relative;
			}

			button {
				font-family: var(--font-family-light);
				font-size: var(--font-size-widget);
				color: var(--color-text-widget-title);
				height: var(--nav-button-height);
				width: var(--nav-button-height);
				text-transform: uppercase;
				letter-spacing: 1px;
				background-color: var(--color-widget-background);
				border: none;
				cursor: pointer;
				line-height: 0;
				transition: background-color 0.4s ease-out, color 0.4s ease-out;
			}
			button:hover {
				background-color: var(--color-widget-background-hover);
			}

			@media (min-width: 768px) {
				.price {
					margin-right: var(--large-padding);
				}
			}

			.dashboard {
				margin-right: var(--padding);
				padding: 0;
				color: var(--color-grey4);
				display: none;
			}

			@media (min-width: 768px) {
				.dashboard {
					display: block;
				}
			}

			.active {
				background-color: var(--color-widget-background-selected);
			}


			.specsheet {
				color: var(--color-grey5);
				border: none;
				padding: 0 var(--small-padding);
			}

			.specsheet.active {
				background-color: var(--color-grey2);
				color: var(--color-black);
				cursor: pointer;
			}

			.specsheet.active .badge {
				display: none;
			}

			.desktop-only {
				display: flex;
				margin-left: auto;
			}

			@media (max-width: 768px) {

				.badge {
					background-color: var(--color-warn);
					color: var(--color-white);
					border-radius: 5px;
					font-size: 12px;
					padding: 1px 4px;
					pointer-events: none;
				}

				.badge.satisfied {
					background-color: transparent
				}

				.desktop-only {
					display: none;
				}
			}
		`
	];

	constructor() {
		super();
		// close dashboard when changing to small breakpoint
		window.matchMedia('(max-width: 768px)').addEventListener('change', (e) => {
			if (e.matches) {
				this.closeDashboard();
			}
		});
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.dashboardActive = state.status.dashboardActive;
		this.specsheetActive = state.status.specsheetActive || this.state.status.media === 'summary';
		this.settingsActive = state.status.settingsActive;

		this.featureModelIsSatisfied = this.featureModelCompletionPercentage() >= 100; // state.decisions.data.isSatisfied;

		this.decisions = state.decisions.data;
		this.productData = state.productData.productData;
		this.features = state.featureModel.data.features;
		this.lang = state.status.lang;
		this.region = state.status.region;
		this.mode = state.status.mode;
		this.forceOverview = state.status.forceOverview;
		this.models = state.productData.models.models;
		this.productModel = state.configuration.data?.projects[0]?.project?.productModelId;
		this.price = state.price.data;
		this.iframe = state.status.iframe;
		this.loggedIn = state.userProfile.data.loggedIn;
		this.family = state.status.family;
	}

	closeDashboard() {
		if (this.dashboardActive) {
			store.dispatch(setMedia({media: 'sections'}));
		}
	}

	toggleDashboard(e) {
		console.log('toggleDashboard');
		this.navigate(routeUrl({media: this.dashboardActive && !this.forceOverview ? 'sections' : 'overview'}));
	}
	toggleSettings(e) {
		store.dispatch(toggleSettingsActive());
	}
	toggleSpecsheet(e) {
		if (this.specsheetActive) {
			this.navigate(routeUrl({media: this.forceOverview ? 'overview' : 'sections'}));
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				this.navigate(routeUrl({media: 'specsheet'}));
			}
		}
	}

	featureModelCompletionPercentage() {
		if (this.features) {
			const visibleCount = this.features
				.filter(feature => getProductData(feature.variableId, this.productData)?.isVisible)
				.length ?? 100;
			const satisfiedCount = this.features
				.filter(feature => getProductData(feature.variableId, this.productData)?.isVisible && isSatisfied(feature.variableId, this.decisions))
				.length ?? 0;

			return Math.round((satisfiedCount / visibleCount * 100));
		} else {
			return 0;
		}
	}

	render() {

		return html`

			${when(!this.iframe,
				() => html`
					${when(
						!this.specsheetActive && !(this.family === 'Start' && !this.loggedIn),
						() => html`<sections-tabs></sections-tabs>`)
					}
					<div class="toolbar">
						<total-price class="desktop-only"></total-price>
						${when(this.settingsActive, () => html`<login-pop-up></login-pop-up>`)};
						<toolbar-button .icon=${gear} .active=${this.settingsActive} .action=${this.toggleSettings}></toolbar-button>
						${when(!this.specsheetActive && !this.forceOverview && this.state.status.family !== 'LightProfile',
							() => html`
								<toolbar-button .icon=${overview} .active=${this.dashboardActive} .action=${this.toggleDashboard.bind(this)}></toolbar-button>
							`)
						}
						${when(this.specsheetActive,
							() => html`
								<toolbar-button .icon=${close} .active=${this.dashboardActive} .action=${this.toggleSpecsheet.bind(this)}></toolbar-button>`,
							() => html`
								${when(this.state.status.family === 'LightProfile',
									() => html`
										${when(selectLightProfileIsSatisfied(this.state),
											() => html`
												<toolbar-button label="${msg('Specsheet')}" .disabled=${!selectLightProfileIsSatisfied(this.state)}
																.action=${this.toggleSpecsheet.bind(this)}></toolbar-button>`,
										)}`,
									() => html`
										${when(this.featureModelIsSatisfied,
											() => html`<toolbar-button label="${msg('Specsheet')}" .disabeled=${!this.featureModelIsSatisfied}
																	   .action=${this.toggleSpecsheet.bind(this)}></toolbar-button>`,
											() => html`
												<!-- <progress-bar progress=${selectFeatureModelCompletionPercentage(this.state)}></progress-bar> -->`,
										)}`,
								)}

							`)
						}
					</div>
				`)
			}





		`;
	}
}
